<template>
  <div>
    <Loading text="Loading page details..." v-if="!loadStatus" />

    <template v-else>
      <div class="holder">
        <h2 class="page-titles">{{ groupOrderDetails.name }}</h2>
      </div>

      <GroupOrderHeaderBanner
        :title="'Set page access'"
        :headerText="'Page Access'"
        :currentStep="creatingGroupOrderStep"
        @onPrevStep="$router.go(-1)"
        @onNextStep="handleNext"
      />

      <!-- Information Fields here -->
      <div class="review-sections">
        <div class="info-holder">
          <!-- Required Field here -->
          <div class="info">
            <section class="general-settings">
              <div class="info-holder-header">
                <div class="flex items-center gap-2">
                  <strong>Sign-in method</strong>
                  <ErrorDisplay
                    :error="true"
                    :errorMessage="pageAccessError.message"
                    v-if="pageAccessError.error"
                  />
                </div>
                <small
                  >Select how users can sign in to your redemption page.</small
                >
              </div>
            </section>

            <GroupOrderLoginMethods
              ref="GroupOrderLoginMethods"
              @onValidate="error => (pageAccessError = error)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import GroupOrderHeaderBanner from './GroupOrderHeaderBanner'
import GroupOrderLoginMethods from './LoginMethods'
import ErrorDisplay from './LoginMethods/ErrorDisplay.vue'
import { mapState } from 'vuex'
import {
  layouts,
  progressStatus,
  isCard,
  handleBrokenImage
} from '../../helpers'

export default {
  name: 'GroupOrderPageAccess',
  props: ['id', 'isEdit'],

  components: { GroupOrderHeaderBanner, GroupOrderLoginMethods, ErrorDisplay },

  data() {
    return {
      customFields: [],
      selectedProducts: [],
      handleBrokenImage: handleBrokenImage,
      pageAccessError: {}
    }
  },

  computed: {
    instantFinalizeState() {
      switch (this.groupOrderDetails.state) {
        case 'pending':
          return false
        case 'locked':
          return false
        case 'finalized':
          return false
        case 'auto_finalising':
          return true
        case 'validating_recipients':
          return true
        case 'validating_subscription':
          return true
        case 'lock_finalisation':
          return false
        default:
          return false
      }
    },
    loadStatus() {
      return progressStatus(this.loadGroupOrder)
    },
    variantId() {
      return this.groupOrderDetails &&
        this.groupOrderDetails.cardsBlob &&
        this.groupOrderDetails.cardsBlob[0]
        ? this.groupOrderDetails.cardsBlob[0].productVariantId
        : ''
    },
    selectedCard() {
      return this.cardList.filter(card => card.id === this.variantId)
    },

    defaultTitle() {
      const enableCustomTitle = this.groupOrderDetails.enableCustomTitle

      if (enableCustomTitle) {
        return this.groupOrderDetails.redemptionTitle
      } else {
        return `Welcome to ${this.groupOrderDetails.name} for ${this.groupOrderDetails.teamName}`
      }
    },

    defaultSubTitle() {
      return this.groupOrderDetails.redemptionSubheading
        ? this.groupOrderDetails.redemptionSubheading
        : 'N/A'
    },

    ...mapState({
      loadGroupOrder: state => state.groupOrder.loadGroupOrder,
      groupOrderDetails: state => state.groupOrder.groupOrderDetails,
      groupOrderTiers: state => state.groupOrder.groupOrderTiers,
      cardList: state => state.cardTemplate.cardList,
      cardMessage: state => state.groupOrder.groupOrderDetails.cardMessage,
      creatingGroupOrderStep: state => state.groupOrder.creatingGroupOrderStep,
      teamName: state => state.team.team.name
    })
  },

  created() {
    this.$store.dispatch('groupOrder/getGroupOrder', this.id)
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.$store.dispatch('cardTemplate/getCards')
    this.$store.commit('groupOrder/CREATING_GROUP_ORDER_STEP', 6)
    this.organizeTiers()
  },

  methods: {
    instantFinalize() {
      if (this.instantFinalizeState) {
        this.$store.dispatch(
          'groupOrder/stopInstantFinalize',
          this.groupOrderDetails.id
        )
      } else {
        this.$store.dispatch(
          'groupOrder/startInstantFinalize',
          this.groupOrderDetails.id
        )
      }
    },
    addCustomField() {
      this.customFields.push({ name: '', type: 'text' })
    },
    isCard(tagList) {
      return isCard(tagList)
    },
    async updateGroupOrder() {
      await this.$store.dispatch('groupOrder/updateGroupOrder', {
        customFields: [
          ...this.groupOrderDetails.customFields,
          ...this.customFields
        ],
        redirect: false,
        showPrices: this.groupOrderDetails.showPrices,
        password: this.groupOrderDetails.password,
        passwordProtected: this.groupOrderDetails['passwordProtected?'],
        redemptionTitle: this.groupOrderDetails.redemptionTitle,
        redemptionDescription: this.groupOrderDetails.redemptionDescription,
        enableCustomTitle: this.groupOrderDetails.enableCustomTitle,
        enableCustomSubTitle: this.groupOrderDetails.enableCustomSubheading,
        redemptionSubTitle: this.groupOrderDetails.redemptionSubheading,
        enableCustomDescription: this.groupOrderDetails.enableCustomDescription
      })
      localStorage.removeItem('selecteCard')
    },
    toggleGroupOrderShowPrices(showPrices, redirect) {
      this.$store.dispatch('groupOrder/updateGroupOrder', {
        customFields: [
          ...this.groupOrderDetails.customFields,
          ...this.customFields
        ],
        redirect: redirect,
        showPrices,
        password: this.groupOrderDetails.password,
        passwordProtected: this.groupOrderDetails['passwordProtected?'],
        redemptionTitle: this.groupOrderDetails.redemptionTitle,
        redemptionDescription: this.groupOrderDetails.redemptionDescription,
        enableCustomTitle: this.groupOrderDetails.enableCustomTitle,
        enableCustomDescription: this.groupOrderDetails.enableCustomDescription
      })
    },
    organizeTiers() {
      // list and organize tiers
      if (!this.loadStatus) {
        for (let i = 0; i <= this.groupOrderTiers.length - 1; i++) {
          const data =
            this.groupOrderTiers &&
            this.groupOrderTiers[i] &&
            this.groupOrderTiers[i]
              ? this.groupOrderTiers[i].tierItems
              : []
          const singleTier = data.map(tier => {
            return {
              tier: i + 1,
              id: tier.itemId || '',
              image:
                tier.itemableAttributes.images &&
                tier.itemableAttributes.images[0]
                  ? tier.itemableAttributes.images[0].url
                  : (tier.itemableAttributes.default_product_variant_images &&
                      tier.itemableAttributes
                        .default_product_variant_images[0] &&
                      tier.itemableAttributes.default_product_variant_images[0]
                        .url) ||
                    '',
              name:
                tier && tier.itemableAttributes
                  ? tier.itemableAttributes.name
                  : '',
              price:
                tier && tier.itemableAttributes
                  ? tier.itemableAttributes.retail_price
                  : tier.itemableAttributes.lowest_price || '',
              tag_list:
                (tier &&
                  tier.itemableAttributes &&
                  tier.itemableAttributes.tag_list) ||
                []
            }
          })

          this.selectedProducts.push(...singleTier)
        }
      }
    },
    handleNext() {
      const pageAccessError = this.$refs.GroupOrderLoginMethods.validate()
      if (!pageAccessError && pageAccessError !== undefined) {
        this.$router.push({
          name: 'reviewOrder',
          params: { id: this.id, isEdit: this.isEdition }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.margin {
  margin-bottom: 1.5rem;
}

// .header-region {
//   p {
//     text-align: center;
//     padding-bottom: 0.6rem;
//     font-size: 1.3rem;
//     font-weight: 400;
//   }
// }

.toggle-price {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  font-size: 21px;

  span {
    font-weight: 500;
    font-size: 0.875rem;
  }
}

.img-holder {
  width: 98px;
  height: 98px;
  max-width: 100%;
  margin: auto;

  img {
    width: inherit;
    height: inherit;
    object-fit: contain;
  }
}

.review-sections {
  margin: 0rem 0rem 0rem 0rem;
  text-align: left;

  .captions {
    font-size: 0.938rem;
    color: #25282a;
    font-weight: bold;
  }

  &.card-review {
    table {
      tbody > tr > td {
        padding: 1rem 0;
      }
      thead > tr > th {
        &:nth-child(2) {
          text-align: left;
          padding-left: 2rem;
        }
        &:nth-child(3) {
          text-align: left;
          // padding-left: 2rem;
        }
      }
      tbody > tr > td {
        &:nth-child(1) {
          width: 8rem;
          max-width: 8rem;
        }
        &:nth-child(2) {
          width: 13rem;
          max-width: 13rem;
          text-align: left;
          padding-left: 2rem;
        }
        &:nth-child(3) {
          width: 22rem;
          max-width: 22rem;
          // padding-left: 2rem;
        }
      }
    }
  }
}

.holder {
  text-align: left;

  h3 {
    font-size: 1.063rem;
    font-family: $inter;
    color: $main-6;
    padding-bottom: 2rem;
    padding-top: 0.5rem;

    span {
      font-weight: 400;
    }
  }
}

.info-holder {
  margin: 1rem 0rem;
  padding: 2rem 1.5rem;
  border: 1px solid #232220;
  border-radius: 12px;

  .required-info {
    display: flex;
    gap: 0rem;

    &.mt-3 {
      margin-top: 2rem;
    }

    .title {
      width: 13%;

      p {
        font-size: 0.838rem;
      }
    }

    .detail {
      width: 80%;

      .display-text {
        font-size: 0.813rem;
        color: $main-6;
        font-weight: 400;
        margin-bottom: 0.25rem;
      }

      p {
        text-align: justify;
        margin-bottom: 1.25rem;
      }

      &.custom-fields {
        ul {
          gap: 0.5rem;
          margin-left: 1rem;

          li {
            font-size: 0.838rem;
            line-height: 20px;
            list-style: disc;
          }
        }
      }
    }
  }
  .info {
    // padding-bottom: 1rem;
    // border-bottom: 1px solid #dfdad2;

    &.info-margin {
      margin-top: 1.5rem;
    }
  }
  .info-holder-header {
    font-size: 0.913rem;
    margin-bottom: 1.2rem;

    small {
      display: block;
      color: #514d43;
      padding-top: 0.5rem;
    }
  }
  .info-holder-sub-header {
    font-size: 0.838rem;
    margin-bottom: 1.2rem;
    color: #a09c95;
    font-weight: 600;
  }
}

.row {
  display: flex;
  text-align: left;
  flex-wrap: wrap;
  justify-content: space-between;

  .row-holder {
    width: 33.3%;
  }
}

.field {
  color: $main-6;
  font-weight: 400;
  font-size: 0.813rem;
  margin-bottom: 0.5rem;
}

.button-area {
  display: flex;
  justify-content: center;
  align-items: center;

  button {
    text-transform: uppercase;
    padding: 0 2rem !important;
  }
}

table {
  width: 100%;
  margin-top: 1.25rem;
  border-spacing: 0px;
  border-collapse: separate;

  thead > tr > th {
    color: $main-6;
    text-align: center;
    padding: 1rem 0rem;
    font-size: 0.813rem;
    border-bottom: 1px solid $onyx;

    &:nth-child(3) {
      text-align: left;
    }
    &:nth-child(4) {
      text-align: left;
    }
    &:nth-child(6) {
      text-align: left;
    }
  }

  tbody > tr > td {
    color: $main-6;
    text-align: center;
    padding: 0.5rem 0rem;
    font-size: 0.813rem;
    border-bottom: 1px solid $onyx;
    width: max-content;

    tfoot > tr > td {
      padding-top: 3rem;
    }

    &:nth-child(1) {
      width: 5rem;
      max-width: 5rem;
    }
    &:nth-child(2) {
      width: 10rem;
      max-width: 10rem;
    }
    &:nth-child(3) {
      text-align: left;
    }
    &:nth-child(4) {
      text-align: left;
    }
    &:nth-child(6) {
      text-align: left;
    }

    &.blank {
      width: 2rem;
    }
  }
}

@media (max-width: 1100px) {
  .required-info {
    transition: 1s ease-in;

    .title {
      width: 13%;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }

    .row {
      .row-holder {
        width: 100%;
      }
    }
  }

  .field {
    flex-basis: 50%;
  }
}
@media (max-width: 982px) {
  .required-info {
    transition: 1s ease-in;

    .title {
      width: 18% !important;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }
  }
}
@media (max-width: 649px) {
  .required-info {
    flex-direction: column;
    transition: 1s ease-in;

    .title {
      width: 100% !important;
      p {
        font-size: 0.838rem;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
